import type {
  AgentMinerSession,
  AgentMinerSessionLinkedTask,
  AgentSessionLabel,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { CATEGORY_COLORS, type CategoryColors, UNCLASSIFIED_COLORS } from './category-chip.type';

/**
 * Normalizes a label part by trimming whitespace.
 * @param {string | null | undefined} value - The label part to normalize.
 * @returns {string | undefined} The normalized label part, or `undefined` if the input is empty.
 */
const normalizeLabelPart = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/**
 * Formats a task line by concatenating the code and name, or returning the task name or code if one is missing.
 * @param {AgentMinerSessionLinkedTask} task - The task to format.
 * @returns {string} The formatted task line.
 */
const formatTaskLine = (task: AgentMinerSessionLinkedTask): string => {
  const code = normalizeLabelPart(task.task_code);
  const name = normalizeLabelPart(task.task_name);
  if (code && name) return `${code}: ${name}`;
  return code ?? name ?? 'Task';
};
/**
 * Resolves the best display label for an agent session, preferring the Flora
 * annotation title set by the agent pipeline, falling back to the persisted
 * session name, then to a truncated session id.
 *
 * @param {Pick<AgentMinerSession, 'id' | 'name' | 'flora_annotations'>} session - Session to derive a label for.
 * @returns {string} A non-empty display label.
 */
const getSessionDisplayTitle = (session: Pick<AgentMinerSession, 'id' | 'name' | 'flora_annotations'>): string => {
  const annotationTitle = normalizeLabelPart(session.flora_annotations?.title);
  if (annotationTitle) return annotationTitle;
  const name = normalizeLabelPart(session.name);
  if (name) return name;
  return session.id.slice(0, 8);
};

/**
 * Renders a Flora annotation label (e.g. `"bug_fix"`) as a human-readable
 * string (`"Bug fix"`). Returns `undefined` when the value is empty so callers
 * can conditionally skip rendering the row.
 *
 * @param {string | null | undefined} value - Raw snake_case label from the backend.
 * @returns {string | undefined} Sentence-cased label, or `undefined` if absent.
 */
const formatAnnotationLabel = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const spaced = trimmed.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

/**
 * Look up the chip color triple (text/border/background) for a Flora annotation
 * label, falling back to the neutral unclassified palette for unknown values.
 *
 * @param {string | null | undefined} label - Raw label string from `flora_annotations.label`.
 * @returns {CategoryColors} Color triple to drive the chip styling.
 */
const getCategoryColors = (label: string | null | undefined): CategoryColors => {
  if (!label) return UNCLASSIFIED_COLORS;
  return CATEGORY_COLORS[label as AgentSessionLabel] ?? UNCLASSIFIED_COLORS;
};

export { formatAnnotationLabel, formatTaskLine, getCategoryColors, getSessionDisplayTitle, normalizeLabelPart };
